import React, { FC, useEffect, useRef, useState } from 'react'
import * as S from './DatasetTabs.style'
import { Dataset, DatasetVersion } from '../../../../api/generated'
import { DownArrowIcon } from '../../../../../../uiKit/icons/Icons'

interface DatasetTabsProps {
  data: Dataset[]
  selectedDatasetIndex: number | null
  onSelectDataset: (index: number) => void
  versionSelections: Record<number, DatasetVersion>
  onVersionChange: (datasetIndex: number, versionId: string) => void
}

export const DatasetTabs: FC<DatasetTabsProps> = ({
  data,
  selectedDatasetIndex,
  onSelectDataset,
  versionSelections,
  onVersionChange,
}) => {
  const [openedDropdownIndex, setOpenedDropdownIndex] = useState<number | null>(null)
  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({})

  const handleOutsideClick = (e: MouseEvent) => {
    if (!Object.values(dropdownRefs.current).some(ref => ref?.contains(e.target as Node))) {
      setOpenedDropdownIndex(null)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick)
    return () => document.removeEventListener('click', handleOutsideClick)
  }, [])

  return (
    <S.TabWrapper>
      {data.map((dataset, idx) => {
        const fallbackVersion = dataset.activeVersionDescription || dataset.allDatasetVersions[0]
        const selectedVersion =
          versionSelections[idx] ||
          ({
            ...fallbackVersion,
            datasetName: dataset.datasetName,
          } as DatasetVersion)

        const versions = dataset.allDatasetVersions
        const hasMultipleVersions = versions.length > 1
        const isSelected = idx === selectedDatasetIndex
        const isDropdownOpen = openedDropdownIndex === idx

        return (
          <div key={idx} style={{ position: 'relative' }} ref={el => (dropdownRefs.current[idx] = el)}>
            <S.DatasetTab selected={isSelected} onClick={() => onSelectDataset(idx)}>
              <S.ActivityDot active={selectedVersion?.isActiveVersion} />
              <S.TabContent>
                <S.DatasetName>
                  {dataset.datasetName}
                  {selectedVersion?.datasetVersionLabel && (
                    <span style={{ color: '#888', fontSize: 13, marginLeft: 6 }}>
                      {selectedVersion.datasetVersionLabel}
                    </span>
                  )}
                </S.DatasetName>
                {hasMultipleVersions && (
                  <S.DropdownIcon
                    isOpen={isDropdownOpen}
                    onClick={e => {
                      e.stopPropagation()
                      setOpenedDropdownIndex(prev => (prev === idx ? null : idx))
                    }}>
                    <DownArrowIcon />
                  </S.DropdownIcon>
                )}
              </S.TabContent>
            </S.DatasetTab>

            {isDropdownOpen && (
              <S.Dropdown>
                {versions.map(version => {
                  const isSelected = selectedVersion.datasetId === version.datasetId

                  return (
                    <S.DropdownItem
                      key={version.datasetId}
                      selected={isSelected}
                      onClick={() => {
                        onVersionChange(idx, version.datasetId)
                        setOpenedDropdownIndex(null)
                      }}>
                      <S.ActivityDot active={version.isActiveVersion} />
                      {dataset.datasetName}
                      {version.datasetVersionLabel && (
                        <span style={{ color: '#888', fontSize: 13, marginLeft: 6 }}>
                          {version.datasetVersionLabel}
                        </span>
                      )}
                    </S.DropdownItem>
                  )
                })}
              </S.Dropdown>
            )}
          </div>
        )
      })}
    </S.TabWrapper>
  )
}
